import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Autocomplete,
  CircularProgress,
} from "@mui/material";
import {
  Close as CloseIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Check as CheckIcon,
  Search as SearchIcon,
} from "@mui/icons-material";
import { BASE_URL } from "../../../services/configUrls";
import CreateTextStatusModal from "./CreateTextStatusModal";

const FeaturesModal = ({
  open,
  membershipName,
  membershipId,
  features,
  onClose,
  onOpenAddFeature,
  onFeaturesUpdated,
  setSnackbar,
}) => {
  const [featureListSearchQuery, setFeatureListSearchQuery] = useState("");
  const [editingFeature, setEditingFeature] = useState(null);
  const [tempStatusValue, setTempStatusValue] = useState("");
  const [updateLoading, setUpdateLoading] = useState(null);
  const [apiStatusOptions, setApiStatusOptions] = useState([]);
  const [deleteFeatureModal, setDeleteFeatureModal] = useState({
    open: false,
    featureId: null,
    featureName: "",
    membershipId: null,
  });
  const [openCreateTextStatusDialog, setOpenCreateTextStatusDialog] =
    useState(false);
  const [newStatusText, setNewStatusText] = useState("");
  const [newStatusSymbol, setNewStatusSymbol] = useState("");
  const [currentEditingFeature, setCurrentEditingFeature] = useState(null);

  // Count validation error states
  const [countError, setCountError] = useState(false);
  const [countErrorMessage, setCountErrorMessage] = useState("");

  useEffect(() => {
    if (open) {
      fetchStatusOptions();
    }
  }, [open]);

  const fetchStatusOptions = async () => {
    try {
      const accessToken = localStorage.getItem("accessToken");

      const response = await fetch(`${BASE_URL}/accounts/all-status-options`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      const result = await response.json();

      if (Array.isArray(result)) {
        const transformedStatuses = result.map((status) => ({
          id: status.id,
          text: status.status_text,
          symbol: status.symbol,
          color: getColorForSymbol(status.symbol),
        }));
        setApiStatusOptions(transformedStatuses);
      }
    } catch (error) {
      console.error("Error fetching status options:", error);
    }
  };

  const getColorForSymbol = (symbol) => {
    switch (symbol) {
      case "✓":
        return { bg: "#D1FAE5", color: "#065F46" };
      case "✗":
        return { bg: "#F3F4F6", color: "#374151" };
      case "*":
        return { bg: "#DBEAFE", color: "#1E40AF" };
      default:
        return { bg: "#E0E7FF", color: "#3730A3" };
    }
  };

  const getStatusColor = (type) => {
    if (type?.toLowerCase() === "count") {
      return { bg: "#E0E7FF", color: "#3730A3" };
    }
    return { bg: "#D1FAE5", color: "#065F46" };
  };

  const getStatusDisplay = (statusValue, type, symbol) => {
    if (type?.toLowerCase() === "count") {
      return statusValue;
    }
    if (symbol && symbol !== "null") {
      return `${symbol} ${statusValue}`;
    }
    return statusValue;
  };

  // const handleFeatureEdit = (index, feature) => {
  //   setEditingFeature(index);
  //   setTempStatusValue(feature.status_value);
  //   // Reset error states
  //   setCountError(false);
  //   setCountErrorMessage("");
  // };
  const handleFeatureEdit = (feature) => {
    setEditingFeature(feature.feature_id); // ✅ use unique id
    setTempStatusValue(feature.status_value);
    setCountError(false);
    setCountErrorMessage("");
  };

  const handleFeatureEditCancel = () => {
    setEditingFeature(null);
    setTempStatusValue("");
    // Reset error states
    setCountError(false);
    setCountErrorMessage("");
  };

  const handleFeatureEditSave = async () => {
    const feature = features.find(
      (f) => f.feature_id === editingFeature
    );

    if (!feature) return;

    // Check for count error before saving
    if (countError) {
      setSnackbar({
        open: true,
        message: "Please fix the errors before saving",
        severity: "error",
      });
      return;
    }

    const statusValueStr = String(tempStatusValue);

    if (feature.feature_type === "Count") {
      if (isNaN(tempStatusValue) || statusValueStr.trim() === "") {
        setSnackbar({
          open: true,
          message: "Please enter a valid number for count",
          severity: "error",
        });
        return;
      }
    } else {
      if (!statusValueStr.trim()) {
        setSnackbar({
          open: true,
          message: "Please enter a status value",
          severity: "error",
        });
        return;
      }
    }

    setUpdateLoading(editingFeature); // ✅ use id

    try {
      const accessToken = localStorage.getItem("accessToken");

      let payload;

      if (feature.feature_type === "Count") {
        payload = {
          membership_id: membershipId,
          feature_id: feature.feature_id,
          count_value: parseInt(tempStatusValue),
        };
      } else {
        const selectedStatus = apiStatusOptions.find(
          (status) => status.text === statusValueStr.trim()
        );

        if (!selectedStatus) {
          setSnackbar({
            open: true,
            message: "Please select a valid status from the list",
            severity: "error",
          });
          setUpdateLoading(null);
          return;
        }

        payload = {
          membership_id: membershipId,
          feature_id: feature.feature_id,
          status_option_id: selectedStatus.id,
        };
      }

      const response = await fetch(
        `${BASE_URL}/accounts/membership-feature-update`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify(payload),
        }
      );

      const result = await response.json();

      if (response.ok) {
        setSnackbar({
          open: true,
          message: result.message || "Feature updated successfully",
          severity: "success",
        });

        setEditingFeature(null);
        setTempStatusValue("");
        setCountError(false);
        setCountErrorMessage("");

        if (onFeaturesUpdated) {
          await onFeaturesUpdated();
        }
      } else {
        setSnackbar({
          open: true,
          message: result.message || "Failed to update feature",
          severity: "error",
        });
      }
    } catch (error) {
      console.error("Error updating feature:", error);
      setSnackbar({
        open: true,
        message: "Error updating feature. Please try again.",
        severity: "error",
      });
    } finally {
      setUpdateLoading(null);
    }
  };
  const handleDeleteFeatureClick = (feature) => {
    setDeleteFeatureModal({
      open: true,
      featureId: feature.feature_id,
      featureName: feature.feature_name,
      membershipId: membershipId,
    });
  };

  const handleCloseDeleteFeatureModal = () => {
    setDeleteFeatureModal({
      open: false,
      featureId: null,
      featureName: "",
      membershipId: null,
    });
  };

  const handleDeleteFeature = async () => {
    try {
      const accessToken = localStorage.getItem("accessToken");

      const response = await fetch(
        `${BASE_URL}/accounts/membership-feature?membership_id=${deleteFeatureModal.membershipId}&feature_id=${deleteFeatureModal.featureId}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      const result = await response.json();

      if (response.ok) {
        setSnackbar({
          open: true,
          message: result.message || "Feature removed successfully",
          severity: "success",
        });

        if (onFeaturesUpdated) {
          await onFeaturesUpdated();
        }
      } else {
        setSnackbar({
          open: true,
          message: result.message || "Failed to remove feature",
          severity: "error",
        });
      }
    } catch (error) {
      console.error("Error removing feature:", error);
      setSnackbar({
        open: true,
        message: "Error removing feature. Please try again.",
        severity: "error",
      });
    } finally {
      handleCloseDeleteFeatureModal();
    }
  };

  return (
    <>
      <Dialog open={open} onClose={(event, reason) => {
        if (editingFeature !== null) return; // block close
        if (features && features.length > 0) {
          onClose();
        }
      }} maxWidth="md" fullWidth>
        <DialogTitle
          sx={{
            fontSize: "15px",
            fontWeight: 600,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            pb: 1,
          }}
        >
          <Box>
            Features - {membershipName}
            <Typography
              variant="caption"
              sx={{ display: "block", color: "#6C757D", fontWeight: 400 }}
            >
              {features.length} feature(s) available
            </Typography>
          </Box>
          {/* <IconButton size="small" onClick={onClose}> */}
          <IconButton
            size="small"
            onClick={() => {
              if (editingFeature !== null) {
                setSnackbar({
                  open: true,
                  message: "Please save or cancel the current edit first",
                  severity: "warning",
                });
                return;
              }
              onClose();
            }}
          >
            <CloseIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {features.length === 0 ? (
            <Box sx={{ py: 4, textAlign: "center" }}>
              <Typography sx={{ color: "#6C757D", fontSize: "13px", mb: 2 }}>
                No features added to this membership
              </Typography>
              <Button
                startIcon={<AddIcon />}
                onClick={() => onOpenAddFeature(membershipId)}
                variant="contained"
                size="small"
              >
                Add Your First Feature
              </Button>
            </Box>
          ) : (
            <>
              <Box
                sx={{
                  display: "flex",
                  gap: 2,
                  mb: 2,
                  alignItems: "center",
                }}
              >
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Search features..."
                  value={featureListSearchQuery}
                  onChange={(e) => setFeatureListSearchQuery(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <SearchIcon
                        sx={{ mr: 1, color: "#9CA3AF", fontSize: 20 }}
                      />
                    ),
                  }}
                  sx={{
                    flex: 1,
                    "& .MuiOutlinedInput-root": {
                      fontSize: "13px",
                    },
                  }}
                />
                <Button
                  startIcon={<AddIcon />}
                  onClick={() => onOpenAddFeature(membershipId)}
                  size="small"
                  variant="outlined"
                  sx={{
                    whiteSpace: "nowrap",
                    minWidth: "fit-content",
                  }}
                >
                  Add More
                </Button>
              </Box>

              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ width: "10%" }}>#</TableCell>
                      <TableCell sx={{ width: "35%" }}>Feature Name</TableCell>
                      <TableCell sx={{ width: "15%" }}>Type</TableCell>
                      <TableCell sx={{ width: "30%" }}>Status</TableCell>
                      <TableCell sx={{ width: "10%", textAlign: "center" }}>
                        Action
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {features
                      .filter((feature) =>
                        feature.feature_name
                          .toLowerCase()
                          .includes(featureListSearchQuery.toLowerCase())
                      )
                      .map((feature, index) => (
                        <TableRow key={feature.membership_feature_id}>
                          <TableCell>
                            <Typography
                              sx={{
                                fontSize: "13px",
                                color: "#6C757D",
                                fontWeight: 600,
                              }}
                            >
                              {index + 1}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography
                              sx={{
                                fontSize: "13px",
                                color: "#212529",
                                fontWeight: 500,
                              }}
                            >
                              {feature.feature_name}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={feature.feature_type}
                              size="small"
                              sx={{
                                backgroundColor:
                                  feature.feature_type?.toLowerCase() ===
                                    "count"
                                    ? "#E0E7FF"
                                    : "#FEF3C7",
                                color:
                                  feature.feature_type?.toLowerCase() ===
                                    "count"
                                    ? "#3730A3"
                                    : "#92400E",
                                fontWeight: 600,
                                fontSize: "11px",
                                height: "22px",
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            {editingFeature === feature.feature_id ? (
                              <Box
                                sx={{
                                  display: "flex",
                                  gap: 1,
                                  alignItems: "flex-start",
                                }}
                              >
                                {feature.feature_type === "Count" ? (
                                  <TextField
                                    autoFocus
                                    size="small"
                                    type="number"
                                    value={tempStatusValue}
                                    error={countError}
                                    helperText={
                                      countError ? countErrorMessage : ""
                                    }
                                    onChange={(e) => {
                                      const newValue = e.target.value;

                                      setTempStatusValue(newValue);

                                      const numValue = parseInt(newValue);
                                      if (
                                        newValue === "" ||
                                        isNaN(numValue)
                                      ) {
                                        setCountError(true);
                                        setCountErrorMessage(
                                          "Please enter a valid count"
                                        );
                                      } else if (numValue < 1) {
                                        setCountError(true);
                                        setCountErrorMessage(
                                          "Count must be at least 1"
                                        );
                                      } else {
                                        setCountError(false);
                                        setCountErrorMessage("");
                                      }
                                    }}
                                    onKeyDown={(e) => {
                                      if (
                                        e.key === "-" ||
                                        e.key === "e" ||
                                        e.key === "E" ||
                                        e.key === "+"
                                      ) {
                                        e.preventDefault();
                                      }
                                    }}
                                    placeholder="Enter count"
                                    inputProps={{
                                      min: 1,
                                    }}
                                    sx={{
                                      width: 120,
                                      "& .MuiOutlinedInput-root": {
                                        fontSize: "13px",
                                      },
                                      "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button":
                                      {
                                        WebkitAppearance: "none",
                                        margin: 0,
                                      },
                                      "& input[type=number]": {
                                        MozAppearance: "textfield",
                                      },
                                    }}
                                  />
                                ) : (
                                  <Autocomplete
                                    freeSolo
                                    autoFocus
                                    options={apiStatusOptions
                                      .sort((a, b) => b.id - a.id)
                                      .map((st) => st.text)}
                                    value={
                                      tempStatusValue === "Create New..."
                                        ? ""
                                        : tempStatusValue
                                    }
                                    onChange={(event, newValue) => {
                                      if (newValue) {
                                        setTempStatusValue(newValue);
                                      }
                                    }}
                                    onInputChange={(
                                      event,
                                      newInputValue,
                                      reason
                                    ) => {
                                      if (reason === "input") {
                                        setTempStatusValue(newInputValue);
                                      }
                                    }}
                                    ListboxProps={{
                                      style: { maxHeight: "250px", padding: 0 },
                                    }}
                                    ListboxComponent={React.forwardRef(
                                      (props, ref) => {
                                        const { children, ...other } = props;
                                        return (
                                          <ul
                                            {...other}
                                            ref={ref}
                                            style={{ padding: 0, margin: 0 }}
                                          >
                                            {/* Sticky "Create New" option */}
                                            <Box
                                              component="li"
                                              onClick={() => {
                                                setEditingFeature(feature.feature_id);
                                                setOpenCreateTextStatusDialog(
                                                  true
                                                );
                                              }}
                                              sx={{
                                                position: "sticky",
                                                top: 0,
                                                zIndex: 10,
                                                backgroundColor: "#FFFFFF",
                                                display: "flex",
                                                alignItems: "center",
                                                gap: 1,
                                                padding: "8px 14px",
                                                cursor: "pointer",
                                                borderBottom:
                                                  "1px solid #E5E7EB",
                                                listStyle: "none",
                                                "&:hover": {
                                                  backgroundColor: "#F0F9FF",
                                                },
                                              }}
                                            >
                                              <AddIcon
                                                sx={{
                                                  fontSize: 16,
                                                  color: "#2D7FF9",
                                                }}
                                              />
                                              <Typography
                                                sx={{
                                                  fontSize: "13px",
                                                  color: "#2D7FF9",
                                                  fontWeight: 600,
                                                }}
                                              >
                                                Create New...
                                              </Typography>
                                            </Box>
                                            {/* Scrollable status list */}
                                            {children}
                                          </ul>
                                        );
                                      }
                                    )}
                                    renderOption={(props, option) => {
                                      const statusObj = apiStatusOptions.find(
                                        (st) => st.text === option
                                      );
                                      return (
                                        <li {...props} key={option}>
                                          <Typography sx={{ fontSize: "13px" }}>
                                            {statusObj && statusObj.symbol
                                              ? `${statusObj.symbol} ${statusObj.text}`
                                              : option}
                                          </Typography>
                                        </li>
                                      );
                                    }}
                                    renderInput={(params) => (
                                      <TextField
                                        {...params}
                                        size="small"
                                        placeholder="Select or type"
                                        sx={{
                                          "& .MuiOutlinedInput-root": {
                                            fontSize: "13px",
                                          },
                                        }}
                                      />
                                    )}
                                    sx={{ flex: 1, minWidth: 150 }}
                                  />
                                )}
                                <IconButton
                                  size="small"
                                  onClick={handleFeatureEditSave}
                                  disabled={
                                    countError || updateLoading === feature.feature_id
                                  }
                                  // sx={{
                                  //   color: countError
                                  //     ? "grey.400"
                                  //     : "success.main",
                                  //   "&:hover": {
                                  //     bgcolor: countError
                                  //       ? "transparent"
                                  //       : "success.light",
                                  //   },
                                  //   cursor: countError
                                  //     ? "not-allowed"
                                  //     : "pointer",
                                  //   transition: "all 0.2s ease",
                                  //   mt: 0.5,
                                  //   "&:hover": {
                                  //     transform: countError
                                  //       ? "none"
                                  //       : "scale(1.1)",
                                  //   },
                                  // }}
                                  sx={{
                                    color: countError ? "grey.400" : "success.main",
                                    cursor: countError ? "not-allowed" : "pointer",
                                    transition: "all 0.2s ease",
                                    mt: 0.5,
                                    "&:hover": {
                                      ...(countError
                                        ? {}
                                        : {
                                          bgcolor: "success.light",
                                          transform: "scale(1.1)",
                                        }),
                                    },
                                  }}
                                >
                                  {updateLoading === feature.feature_id ? (
                                    <CircularProgress
                                      size={18}
                                      color="success"
                                    />
                                  ) : (
                                    <CheckIcon sx={{ fontSize: 18 }} />
                                  )}
                                </IconButton>
                                <IconButton
                                  size="small"
                                  onClick={handleFeatureEditCancel}
                                  color="error"
                                  disabled={updateLoading === feature.feature_id}
                                  sx={{
                                    transition: "all 0.2s ease",
                                    mt: 0.5,
                                    "&:hover": {
                                      transform: "scale(1.1)",
                                    },
                                  }}
                                >
                                  <CloseIcon sx={{ fontSize: 18 }} />
                                </IconButton>
                              </Box>
                            ) : (
                              <Box
                                sx={{
                                  display: "inline-flex",
                                  alignItems: "center",
                                  px: 2,
                                  py: 0.5,
                                  borderRadius: "4px",
                                  backgroundColor: getStatusColor(
                                    feature.feature_type
                                  ).bg,
                                  color: getStatusColor(feature.feature_type)
                                    .color,
                                  fontWeight: 500,
                                  fontSize: "13px",
                                  cursor: "pointer",
                                  transition: "all 0.2s ease",
                                  "&:hover": {
                                    opacity: 0.8,
                                    transform: "translateY(-1px)",
                                  },
                                }}
                                onClick={() =>
                                  handleFeatureEdit(feature)
                                }
                              >
                                {getStatusDisplay(
                                  feature.status_value,
                                  feature.feature_type,
                                  feature.symbol
                                )}
                              </Box>
                            )}
                          </TableCell>
                          <TableCell sx={{ textAlign: "center" }}>
                            <IconButton
                              size="small"
                              onClick={() => handleDeleteFeatureClick(feature)}
                              color="error"
                              sx={{
                                transition: "all 0.2s ease",
                                "&:hover": {
                                  transform: "scale(1.1)",
                                },
                              }}
                            >
                              <DeleteIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    {features.filter((feature) =>
                      feature.feature_name
                        .toLowerCase()
                        .includes(featureListSearchQuery.toLowerCase())
                    ).length === 0 && (
                        <TableRow>
                          <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                            <Typography
                              sx={{ color: "#6C757D", fontSize: "13px" }}
                            >
                              No features found matching "{featureListSearchQuery}
                              "
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          {/* <Button onClick={onClose} variant="contained"
          disabled={!features || features.length === 0}
          >
            Confirm
          </Button> */}
          <Button
            onClick={() => {
              if (editingFeature !== null) {
                setSnackbar({
                  open: true,
                  message: "Please save or cancel the current edit first",
                  severity: "warning",
                });
                return;
              }
              onClose();
            }}
            variant="contained"
            //  disabled={!features || features.length === 0}
            disabled={
              !features ||
              features.length === 0 ||
              updateLoading !== null
            }
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Feature Confirmation Modal */}
      <Dialog
        open={deleteFeatureModal.open}
        onClose={handleCloseDeleteFeatureModal}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle
          sx={{
            fontSize: "15px",
            fontWeight: 600,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Box>
            Remove Feature
            <Typography
              variant="caption"
              sx={{ display: "block", color: "#6C757D", fontWeight: 400 }}
            >
              This feature will be removed from this membership
            </Typography>
          </Box>
          <IconButton size="small" onClick={handleCloseDeleteFeatureModal}>
            <CloseIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Typography sx={{ fontSize: "13px", color: "#212529", mb: 2 }}>
            Are you sure you want to remove{" "}
            <strong>"{deleteFeatureModal.featureName}"</strong>?
          </Typography>
          <Typography sx={{ fontSize: "13px", color: "#6C757D" }}>
            This will permanently remove the feature from this membership.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={handleCloseDeleteFeatureModal} variant="outlined">
            Cancel
          </Button>
          <Button
            onClick={handleDeleteFeature}
            variant="contained"
            color="error"
            startIcon={<DeleteIcon sx={{ fontSize: 16 }} />}
          >
            Remove
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create Text Status Modal */}
      <CreateTextStatusModal
        open={openCreateTextStatusDialog}
        onClose={() => {
          setOpenCreateTextStatusDialog(false);
          if (editingFeature !== null) {
            const feature = features.find(
              (f) => f.feature_id === editingFeature
            );
            if (feature) {
              setTempStatusValue(feature.status_value);
            }
          }
        }}
        newStatusText={newStatusText}
        setNewStatusText={setNewStatusText}
        newStatusSymbol={newStatusSymbol}
        setNewStatusSymbol={setNewStatusSymbol}
        onRefreshStatusOptions={fetchStatusOptions}
        onShowSnackbar={setSnackbar}
        onResetTempValue={(newValue) => {
          if (newValue) {
            setTempStatusValue(newValue);
          } else if (editingFeature !== null) {
            const feature = features.find(
              (f) => f.feature_id === editingFeature
            );
            if (feature) {
              setTempStatusValue(feature.status_value);
            }
          }
        }}
      />
    </>
  );
};

export default FeaturesModal;
